/**
 * DangoteBusinessUnit — BU aggregation. Reads from DangoteContext.
 * Shows baseline/project/reduction structure same as Oko-Oba.
 */
import React, { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDangote } from "@/context/DangoteContext";
import { PageHeader, SectionCard, MetricCard } from "@/components/ds";
import { ArrowLeft, ChevronRight, CheckCircle2, Clock, RefreshCw } from "lucide-react";

export default function DangoteBusinessUnit() {
  const { bu_id } = useParams();
  const navigate  = useNavigate();
  const { consolidated, loading, period } = useDangote();

  const bu = useMemo(() => {
    if (!consolidated) return null;
    return consolidated.by_bu?.find(b => b.bu_id === bu_id) || null;
  }, [consolidated, bu_id]);

  if (loading && !consolidated) return (
    <div className="p-6 space-y-4">
      {[1,2,3].map(i=><div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse"/>)}
    </div>
  );
  if (!bu) return (
    <div className="p-6">
      <button onClick={()=>navigate("/corporate/dashboard")} className="text-xs text-slate-500 mb-4 flex items-center gap-1"><ArrowLeft size={12}/> Back</button>
      <p className="text-slate-500">BU not found in current period data.</p>
    </div>
  );

  const total = bu.total || 0;
  const assets = bu.assets || [];

  // Aggregate baselines: industry benchmark × production
  // For BU aggregation: show actual emissions vs industry benchmark
  const BENCHMARK_INTENSITY = { cement:0.65, refinery:0.25, fertilizer:0.45, sugar:0.20, logistics:0.60 };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <button onClick={()=>navigate("/corporate/dashboard")} className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-700 mb-2">
            <ArrowLeft size={12}/> Corporate Dashboard
          </button>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-0.5">
            Dangote Industries · {bu.sector_type}
          </p>
          <PageHeader title={bu.bu_name} />
          <p className="text-xs text-slate-400 mt-0.5">{period?.label} · {assets.length} assets</p>
        </div>
        <span className="text-xs font-bold px-2 py-0.5 rounded-full border bg-teal-100 text-teal-700 border-teal-200 flex-shrink-0 ml-4">
          {period?.label}
        </span>
      </div>

      {/* KPI cards — same structure as Oko-Oba */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label="Total tCO₂e"   value={Number(total).toLocaleString()}   unit="tCO₂e/month" color="text-slate-900" />
        <MetricCard label="Scope 1"       value={Number(bu.scope1||0).toLocaleString()} unit="tCO₂e" color="text-red-700" />
        <MetricCard label="Scope 2"       value={Number(bu.scope2||0).toLocaleString()} unit="tCO₂e" color="text-orange-600" />
        <MetricCard label="Assets Active" value={`${assets.filter(a=>a.total>0).length}/${assets.length}`} color="text-teal-700" />
      </div>

      {/* Scope breakdown bars */}
      <SectionCard>
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Emission Structure</p>
          <span className="text-xs text-slate-400">{Number(total).toLocaleString()} tCO₂e total</span>
        </div>
        {[
          { label: "Scope 1 — Combustion + Process", value: bu.scope1||0, color: "bg-red-400" },
          { label: "Scope 2 — Electricity",           value: bu.scope2||0, color: "bg-orange-400" },
          { label: "Scope 3 — Transport",             value: bu.scope3||0, color: "bg-purple-400" },
        ].filter(x => x.value > 0).map(({ label, value, color }) => {
          const pct = total > 0 ? Math.min((value/total)*100, 100) : 0;
          return (
            <div key={label} className="mb-3">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-600">{label}</span>
                <span className="font-bold text-slate-900">{Number(value).toLocaleString()} tCO₂e ({pct.toFixed(1)}%)</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${color}`} style={{width:`${pct}%`}} />
              </div>
            </div>
          );
        })}
      </SectionCard>

      {/* Asset table */}
      <SectionCard>
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Assets — {period?.label}</p>
          <span className="text-xs text-slate-400">{assets.length} facilities</span>
        </div>
        {assets.map(a => (
          <button key={a.asset_id} onClick={() => navigate(`/corporate/asset/${a.asset_id}`)}
            className="w-full flex items-start justify-between py-3 border-b border-slate-50 last:border-0 hover:bg-slate-50/70 rounded-lg px-2 -mx-2 group text-left transition-colors">
            <div className="flex-1 min-w-0 mr-3">
              <p className="text-sm font-semibold text-slate-900 group-hover:text-teal-700">{a.asset_id}</p>
              <p className="text-xs text-slate-400 mt-0.5">
                S1:{Number(a.scope1||0).toLocaleString()} S2:{Number(a.scope2||0).toLocaleString()}
                {a.scope3>0 && ` S3:${Number(a.scope3).toLocaleString()}`}
                {a.circularity?.afr_substitution_pct > 0 && ` · AFR:${a.circularity.afr_substitution_pct.toFixed(1)}%`}
              </p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="text-right">
                {a.total > 0 ? (
                  <>
                    <p className="text-sm font-bold text-red-700 tabular-nums">{Number(a.total).toLocaleString()}</p>
                    <p className="text-xs text-slate-400">tCO₂e/month</p>
                  </>
                ) : <p className="text-xs text-amber-500 flex items-center gap-1"><Clock size={9}/>Pending</p>}
              </div>
              <ChevronRight size={13} className="text-slate-300 group-hover:text-teal-500" />
            </div>
          </button>
        ))}
      </SectionCard>

      {/* Navigation */}
      <SectionCard>
        <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">Next Actions</p>
        {[
          ["Carbon Strategy", `/corporate/carbon-strategy?bu=${bu_id}`],
          ["Financing", "/corporate/financing"],
          ["Corporate Dashboard", "/corporate/dashboard"],
        ].map(([l,p]) => (
          <button key={p} onClick={() => navigate(p)}
            className="w-full flex items-center justify-between py-2 text-xs text-slate-600 hover:text-teal-700 group">
            {l} <ChevronRight size={11} className="text-slate-300 group-hover:text-teal-500" />
          </button>
        ))}
      </SectionCard>
    </div>
  );
}
